<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useRouter } from 'vue-router';

import searchIcon from '@/assets/icons/search.svg';
import noTransactionsFoundLottieData from '@/assets/lottie/no-transactions-found.json';
import { useAccount } from '@/composables/useAccount';
import { useGatedRef } from '@/composables/useGatedRef';
import { useTransactionDetails } from '@/composables/useTransactionDetails';
import { useTransactions } from '@/composables/useTransactions';
import { useTransactionsView } from '@/composables/useTransactionsView';
import { useViewBreakpoints } from '@/composables/useViewBreakpoints';
import {
  type Transaction,
  TransactionStatusOTC,
  TransactionType
} from '@/references/axios/transactions/types';
import { useI18n } from '@/references/i18n';
import { RouteNames } from '@/router';

import TipModal from '@/components/TipModal.vue';
import TransactionDetailsFeeModal from '@/components/transaction-details/TransactionDetailsFeeModal.vue';
import TransactionDetailsModal from '@/components/transaction-details/TransactionDetailsModal.vue';
import TransactionDetailsSidebar from '@/components/transaction-details/TransactionDetailsSidebar.vue';
import TransactionHistory from '@/components/TransactionHistory.vue';
import UiContainer from '@/components/ui/UiContainer.vue';
import UiInput from '@/components/ui/UiInput.vue';
import UiLottie from '@/components/ui/UiLottie.vue';
import UiNotFound from '@/components/ui/UiNotFound.vue';
import UiSkeleton from '@/components/ui/UiSkeleton.vue';
import UiText from '@/components/ui/UiText.vue';
import UiTitle from '@/components/ui/UiTitle.vue';

interface Props {
  id?: string;
}

const props = defineProps<Props>();

const { groups, reset, total, searchValue, loaded, updateSearchValue } = useTransactionsView();
const { isAuthed } = useAccount();
const { t } = useI18n();
const { push, replace } = useRouter();
const { loading, load: loadTransactions } = useTransactions();
const isGatedLoaded = useGatedRef(loaded);
const {
  transactionId,
  opened: isDetailsOpened,
  statusModalOpened,
  statusModalText,
  open,
  openById,
  close,
  closeStatusModal
} = useTransactionDetails();
const { lessThanXL } = useViewBreakpoints();

const handlingNavigationChange = ref(false);

const isOpened = computed(
  (): boolean => isDetailsOpened.value && !lessThanXL.value && isGatedLoaded.value
);

function handleDetailsModalClosed(): void {
  // don't trigger watcher
  handlingNavigationChange.value = true;
  replace({ name: RouteNames.Activity });
  handlingNavigationChange.value = false;
}

function handleOpenDetails(transaction_: Transaction): void {
  if (
    transaction_.type === TransactionType.OTC &&
    transaction_.status === TransactionStatusOTC.Pending
  ) {
    push({
      name: RouteNames.WatchQuote,
      params: { id: transaction_.quote?.quoteId ?? transaction_.id }
    });
    return;
  }
  // don't trigger watcher
  handlingNavigationChange.value = true;

  replace({ name: RouteNames.Activity, params: { id: transaction_.id } });
  open(transaction_);

  handlingNavigationChange.value = false;
}

watch(
  () => props.id,
  async (id) => {
    if (handlingNavigationChange.value) {
      return;
    }

    if (id === undefined) {
      handlingNavigationChange.value = false;
      return;
    }

    await openById(id);
    handlingNavigationChange.value = false;
  },
  {
    deep: true,
    immediate: true
  }
);

onBeforeUnmount(() => {
  closeStatusModal();
  close();
  reset();
});

if (!loading.value) {
  loadTransactions();
}
</script>

<template>
  <section class="activity-view" :class="{ 'activity-view_opened': isOpened }">
    <UiContainer class="activity-view__container">
      <div class="activity-view__body">
        <UiTitle class="activity-view__title" :level="1">
          {{ t('activity.blockTitle') }}
        </UiTitle>
        <div v-if="total || !isGatedLoaded" class="activity-view__input-wrapper">
          <UiInput
            class="activity-view__input"
            :model-value="searchValue"
            :placeholder="t('activity.searchPlaceholder')"
            :icon="searchIcon"
            @update:model-value="updateSearchValue"
          />
        </div>
        <template v-if="isGatedLoaded">
          <TransactionHistory v-if="groups.length" @open-details="handleOpenDetails" />
          <UiNotFound v-else-if="total" :text="t('activity.notFound')" />
          <div v-else class="activity-view__empty">
            <UiLottie
              class="activity-view__empty-lottie"
              :data="noTransactionsFoundLottieData"
              :size="124"
              loop
              autoplay
            />
            <UiText
              class="activity-view__empty-description"
              tag="p"
              weight="medium"
              size="md"
              align="center"
              muted
            >
              {{ t('activity.emptyHistory') }}
            </UiText>
          </div>
        </template>
        <ul v-else class="activity-view__skeleton-group">
          <li v-for="i in 3" :key="i" class="activity-view__skeleton-row">
            <div class="activity-view__skeleton-col">
              <UiSkeleton border-radius="50%" width="44px" height="44px" />
            </div>
            <div class="activity-view__skeleton-col">
              <UiSkeleton width="100%" height="22px" />
              <UiSkeleton width="75%" height="16px" />
            </div>
            <div class="activity-view__skeleton-col">
              <UiSkeleton width="75%" height="16px" />
              <UiSkeleton width="100%" height="22px" />
            </div>
          </li>
        </ul>
      </div>
      <Transition name="activity-view-details" mode="out-in">
        <div v-if="isOpened" class="activity-view__sidebar-wrapper">
          <Transition name="activity-view-details-item" mode="out-in">
            <TransactionDetailsSidebar
              :key="transactionId"
              class="activity-view__sidebar"
              @close="handleDetailsModalClosed"
            />
          </Transition>
        </div>
      </Transition>
    </UiContainer>
    <TransactionDetailsModal v-if="isGatedLoaded" @close="handleDetailsModalClosed" />
    <TipModal
      :title="t('activity.status.title.modal')"
      :model-value="isAuthed && statusModalOpened && !!statusModalText"
      @update:model-value="closeStatusModal"
    >
      {{ statusModalText }}
    </TipModal>
    <TransactionDetailsFeeModal />
  </section>
</template>

<style scoped>
.activity-view {
  position: relative;
  margin: -40px 0;
}

@media (max-width: 1279px) {
  .activity-view {
    margin: 0;
  }
}

.activity-view__container {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  transition: max-width var(--animation-micro) var(--animation-micro) var(--animation-effect);
}

.activity-view_opened .activity-view__container {
  max-width: 1080px;
  transition: max-width var(--animation-micro) var(--animation-effect);
}

.activity-view__body {
  min-width: 560px;
  max-width: 560px;
  padding: 40px 0;
  transition:
    max-width var(--animation-micro) var(--animation-micro) var(--animation-effect),
    min-width var(--animation-micro) var(--animation-micro) var(--animation-effect);
}

@media (max-width: 1319px) {
  .activity-view_opened .activity-view__body {
    min-width: 500px;
    max-width: 500px;
    transition:
      max-width var(--animation-micro) var(--animation-effect),
      min-width var(--animation-micro) var(--animation-effect);
  }
}

@media (max-width: 1279px) {
  .activity-view .activity-view__body {
    padding: 0;
  }
}

@media (max-width: 919px) {
  .activity-view .activity-view__body {
    width: 100%;
    min-width: 100%;
    max-width: 100%;
  }
}

.activity-view__body:not(:last-child) {
  margin: 0 40px 0 0;
}

.activity-view__sidebar-wrapper {
  position: sticky;
  top: 0;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  max-width: 400px;
  height: 100vh;
  margin: 0 0 0 auto;
  padding: 40px 0;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

@media (hover: hover) {
  .activity-view__sidebar-wrapper {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }

  .activity-view__sidebar-wrapper::-webkit-scrollbar {
    display: none;
  }
}

.activity-view__sidebar {
  flex-grow: 1;
}

.activity-view-details-enter-from,
.activity-view-details-leave-to,
.activity-view-details-item-enter-from,
.activity-view-details-item-leave-to {
  opacity: 0;
}

.activity-view-details-enter-active,
.activity-view-details-leave-active,
.activity-view-details-item-enter-active,
.activity-view-details-item-leave-active {
  will-change: opacity;
}

.activity-view-details-enter-active {
  transition: opacity var(--animation-micro) var(--animation-micro) var(--animation-effect);
}

.activity-view-details-item-enter-active {
  transition: opacity var(--animation-micro) var(--animation-effect);
}

.activity-view-details-leave-active,
.activity-view-details-item-leave-active {
  transition: opacity var(--animation-micro) var(--animation-effect);
}

.activity-view__title {
  position: relative;
  z-index: 2;
  margin: 0 0 24px;
  transform: translateZ(0);
}

.activity-view__input-wrapper {
  position: sticky;
  top: 0;
  left: 0;
  z-index: 1;
  margin: -40px 0 40px;
  padding: 40px 0 0;
  background-color: var(--color-background);
  border-radius: 0 0 12px 12px;
  box-shadow: var(--shadow-background-spread-md);
}

@media (max-width: 919px) {
  .activity-view__input-wrapper,
  .activity-view_opened .activity-view__input-wrapper {
    margin: -16px 0 40px;
    padding: 16px 0 0;
  }
}

.activity-view__empty {
  padding: 80px 0 0;
}

@media (max-width: 919px) {
  .activity-view__empty {
    padding: 40px 0 0;
  }
}

.activity-view__empty-lottie {
  margin: 0 auto 8px;
}

.activity-view__empty-description {
  max-width: 280px;
  margin: 0 auto;
}

.activity-view__skeleton-group {
  margin: 0;
  padding: 16px;
  list-style-type: none;
  background-color: var(--color-main-underlay);
  border-radius: 12px;
}

.activity-view__skeleton-row {
  display: flex;
}

.activity-view__skeleton-row:not(:last-child) {
  margin: 0 0 24px;
}

.activity-view__skeleton-col {
  display: flex;
  flex-direction: column;
}

.activity-view__skeleton-col .ui-skeleton:first-child {
  margin: 0 0 auto;
}

.activity-view__skeleton-col:first-child {
  margin: 0 16px 0 0;
}

.activity-view__skeleton-col:nth-child(2) {
  width: 104px;
  max-width: calc(50% - 40px);
}

.activity-view__skeleton-col:last-child {
  align-items: flex-end;
  width: 90px;
  max-width: calc(50% - 40px);
  margin: 0 0 0 auto;
}
</style>
